import React from 'react';
import type { ResearchQuestion } from '@/features/request-gateway/types';
import {
  answerText,
  answerValues,
  isAnswerFilled,
  isFileQuestionType,
  type PreliminaryDecision,
} from '@/features/request-gateway/researchWizardUtils';

export type AnswerDecision = PreliminaryDecision | 'conflict';
export type ChipStatus =
  | 'SOURCE_CONFIRMED'
  | 'NEEDS_CONFIRMATION'
  | 'USER_CONFIRMED'
  | 'CONFLICT_REVIEW';

const MULTI_TYPES = new Set([
  'multi',
  'multiselect',
  'checkbox',
  'entity_multi',
  'author_multi',
  'service_multi',
  'file_multi',
]);

export function chipStatus(
  question: ResearchQuestion,
  answers: Record<string, unknown>,
  decision?: AnswerDecision,
): ChipStatus {
  if (decision === 'conflict') return 'CONFLICT_REVIEW';
  if (decision === 'edited') return 'USER_CONFIRMED';
  if (decision === 'accepted') return 'SOURCE_CONFIRMED';
  const current = answers[question.id];
  const preliminary = question.preliminary_answer || '';
  if (isAnswerFilled(current) && preliminary && answerText(current) === answerText(preliminary)) {
    return 'SOURCE_CONFIRMED';
  }
  if (isAnswerFilled(current) && preliminary && answerText(current) !== answerText(preliminary)) {
    return 'USER_CONFIRMED';
  }
  return 'NEEDS_CONFIRMATION';
}

function chipClass(status: ChipStatus): string {
  if (status === 'SOURCE_CONFIRMED' || status === 'USER_CONFIRMED') return 'good';
  if (status === 'CONFLICT_REVIEW') return 'bad';
  return 'warn';
}

interface QuestionCardProps {
  question: ResearchQuestion;
  index: number;
  serviceCode: string;
  value: unknown;
  decision?: AnswerDecision;
  showActions?: boolean;
  onChange: (value: unknown) => void;
  onDecision: (decision: AnswerDecision) => void;
  onConfirm: () => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  serviceCode,
  value,
  decision,
  showActions = true,
  onChange,
  onDecision,
  onConfirm,
}) => {
  const status = chipStatus(question, { [question.id]: value }, decision);
  const conditional = Boolean(question.show_if || question.required_if);
  const unique = !question.preliminary_answer && !isAnswerFilled(value);
  const shown = answerValues(question.preliminary_answer).join(', ');

  return (
    <article className="r54-question-card" data-question-id={question.id}>
      <header>
        <div>
          <span className="r54-eyebrow">ВОПРОС {index + 1}{serviceCode ? ` · ${serviceCode}` : ''}</span>
          <h3 id={`r54-label-${question.id}`}>{question.label}{question.required ? ' *' : ''}</h3>
        </div>
        <div className="r54-badges">
          <span className={`r54-chip ${chipClass(status)}`}>{{ SOURCE_CONFIRMED: 'Источник подтверждён', NEEDS_CONFIRMATION: 'Требует подтверждения', USER_CONFIRMED: 'Подтверждено', CONFLICT_REVIEW: 'Требует уточнения' }[status]}</span>
          {conditional ? <span className="r54-chip warn">По условию</span> : null}
          {unique ? <span className="r54-chip bad">Заполните самостоятельно</span> : null}
        </div>
      </header>
      <QuestionControl question={question} value={value} onChange={onChange} />
      {question.help_text ? <p id={`r54-help-${question.id}`} className="r54-note">{question.help_text}</p> : null}
      <aside className="r54-preanswer">
        <small>
          ПРЕДВАРИТЕЛЬНЫЙ ОТВЕТ
        </small>
        <strong className={shown ? undefined : 'r55-empty-answer'}>
          {shown || 'Предварительного ответа нет'}
        </strong>
        {showActions ? (
          <div className="r54-card-actions">
            <button type="button" onClick={() => onDecision('accepted')}>Принять</button>
            <button type="button" onClick={() => onDecision('edited')}>Изменить</button>
            <button type="button" onClick={() => onDecision('cleared')}>Очистить</button>
            <button type="button" onClick={onConfirm}>Подтвердить</button>
            <button type="button" onClick={() => onDecision('conflict')}>Конфликт</button>
          </div>
        ) : null}
      </aside>
    </article>
  );
};

const QuestionControl: React.FC<{
  question: ResearchQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ question, value, onChange }) => {
  const type = String(question.type || 'text').toLowerCase();
  const controlId = `r54-q-${question.id}`;
  const accessibility = {
    'aria-labelledby': `r54-label-${question.id}`,
    'aria-describedby': question.help_text ? `r54-help-${question.id}` : undefined,
    'aria-required': question.required,
  };
  const label = question.prompt || question.label;

  if (isFileQuestionType(type)) {
    return <p className="r54-note">Файл для этого вопроса добавляется на шаге «Материалы».</p>;
  }

  if ((MULTI_TYPES.has(type) || type === 'radio') && question.options.length > 0) {
    const selected = new Set(answerValues(value));
    return (
      <div className="r54-choice-field">
        <p className="r54-note">{type === 'radio' ? 'Выберите один вариант.' : 'Можно выбрать несколько вариантов.'}</p>
        <div className="r54-options" role={type === 'radio' ? 'radiogroup' : 'group'} {...accessibility}>
          {question.options.map((option, optionIndex) => (
            <label key={option}>
              <input
                id={optionIndex === 0 ? controlId : `${controlId}-${optionIndex}`}
                type={type === 'radio' ? 'radio' : 'checkbox'}
                name={controlId}
                checked={type === 'radio' ? answerText(value) === option : selected.has(option)}
                onChange={() => {
                  if (type === 'radio') {
                    onChange(option);
                    return;
                  }
                  const next = new Set(selected);
                  if (next.has(option)) next.delete(option);
                  else next.add(option);
                  onChange([...next]);
                }}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if ((type === 'select' || type === 'author_select') && question.options.length > 0) {
    return (
      <label className="r54-field">
        <span>{label}</span>
        <select id={controlId} {...accessibility} value={answerText(value)} onChange={(event) => onChange(event.target.value)}>
          <option value="">Выберите</option>
          {question.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>
    );
  }

  if (type === 'number' || type === 'range') {
    return (
      <label className="r54-field">
        <span>{label}</span>
        <input id={controlId} {...accessibility} type="number" value={answerText(value)} placeholder={question.placeholder || ''} onChange={(event) => onChange(event.target.value)} />
      </label>
    );
  }

  if (type === 'date') {
    return (
      <label className="r54-field">
        <span>{label}</span>
        <input id={controlId} {...accessibility} type="date" value={answerText(value)} onChange={(event) => onChange(event.target.value)} />
      </label>
    );
  }

  if (type === 'text' || type === 'url' || type === 'email') {
    return (
      <label className="r54-field">
        <span>{label}</span>
        <input id={controlId} {...accessibility} type={type} value={answerText(value)} placeholder={question.placeholder || ''} onChange={(event) => onChange(event.target.value)} />
      </label>
    );
  }

  return (
    <label className="r54-field">
      <span>{label}</span>
      <textarea id={controlId} {...accessibility} value={answerText(value)} placeholder={question.placeholder || ''} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
};
